import React, { Suspense, useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { Canvas } from '@react-three/fiber'
import { MapControls } from '@react-three/drei'
import { useLocation } from 'react-router-dom'
import { useAlert } from '../contexts/AlertContext'
import config from '../config'
import Svg from '../components/viewer/Svg'
import HoverTooltip from '../components/viewer/HoverTooltip'
import { STATUS, statusToColor } from '../utils/status'
import { clearLotImageCache } from '../utils/lotImageExtractor'

// Utility function to detect touch devices
const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0
}

// components extracted to src/components/viewer/*

export default function ViewerPage() {
  const controlsRef = useRef()
  const hasPannedRef = useRef(false)
  const panTimeoutRef = useRef(null)
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const { alert } = useAlert()
  const mapId = queryParams.get('mapId')
  const isAdminMode = queryParams.get('preview') === 'true' // Detect admin/preview mode

  const [svgUrl, setSvgUrl] = useState(null)
  const [backgroundUrl, setBackgroundUrl] = useState(null)
  const [backgroundSvgUrl, setBackgroundSvgUrl] = useState(null)
  const [compassUrl, setCompassUrl] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [mapName, setMapName] = useState(null)
  const [assetsReady, setAssetsReady] = useState(false)
  const [regions, setRegions] = useState([])
  const [statusColors, setStatusColors] = useState({})
  const [stageTemplate, setStageTemplate] = useState('default')
  const [buttonColors, setButtonColors] = useState(null)
  const [hoveredRegion, setHoveredRegion] = useState(null)
  const [selectedRegion, setSelectedRegion] = useState(null)
  const [hoverPosition, setHoverPosition] = useState(null)
  const [viewMode, setViewMode] = useState('tabs') // retained but we'll avoid using 'details'
  const [animateIntro, setAnimateIntro] = useState(false)
  const [cameraZoom, setCameraZoom] = useState(2)
  const [activeStage, setActiveStage] = useState(null) // null = all
  const [hoveredStage, setHoveredStage] = useState(null)
  const [stageLockInfo, setStageLockInfo] = useState({}) // { stageName: { isLocked, displayLabel } }

  // Enquiry form state
  const [showEnquiryForm, setShowEnquiryForm] = useState(false)
  const [showEnquiryFormOverlay, setShowEnquiryFormOverlay] = useState(false)
  const [enquiryForm, setEnquiryForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  })
  const [enquiryErrors, setEnquiryErrors] = useState({})
  const [enquirySubmitted, setEnquirySubmitted] = useState(false)
  const [enquirySubmitting, setEnquirySubmitting] = useState(false)
  const [tenantLogo, setTenantLogo] = useState(null)
  const [tenantName, setTenantName] = useState(null)
  const [tenantContactEmail, setTenantContactEmail] = useState(null)
  const [tenantWebsite, setTenantWebsite] = useState(null)
  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth < 1024)
  const [sidebarVisible, setSidebarVisible] = useState(false)
  const [legendOpen, setLegendOpen] = useState(() => window.innerWidth >= 768)
  const [sidebarAnimating, setSidebarAnimating] = useState(false)
  const [isTouch, setIsTouch] = useState(() => isTouchDevice())

  // State for lot image extraction
  const [hoveredShape, setHoveredShape] = useState(null)
  const [svgViewBox, setSvgViewBox] = useState(null)
  const [backgroundImageElement, setBackgroundImageElement] = useState(null)
  const [overlayImageElement, setOverlayImageElement] = useState(null)
  const [regionShapeMap, setRegionShapeMap] = useState(null)
  const [lotListViewMode, setLotListViewMode] = useState('list') // 'list' | 'grid'
  const [selectedGroup, setSelectedGroup] = useState(null) // { stageName, groupName } for admin mode
  const [hoveredGroup, setHoveredGroup] = useState(null) // { stageName, groupName } for admin mode hover
  const [activeTab, setActiveTab] = useState('available') // Track active tab from StageSidebar

  const StageSidebar = useMemo(() => React.lazy(() => import('../components/viewer/StageSidebar')), [])

  // Set initial zoom based on screen size (zoomed out on tablet/mobile)
  useEffect(() => {
    const computeZoom = () => {
      const w = window.innerWidth
      if (w <= 480) return 0.5 // phones: most zoomed out
      if (w <= 768) return 0.8 // tablets: moderately zoomed out
      return 2 // desktops: default
    }
    setCameraZoom(computeZoom())
    const onResize = () => {
      setCameraZoom(computeZoom())
      const newIsNarrow = window.innerWidth < 1024
      setIsNarrow(newIsNarrow)
      // Hide sidebar on desktop, keep current state on mobile/tablet
      if (!newIsNarrow) {
        setSidebarVisible(false)
      }
      // Legend responsive behavior: collapse on small screens, expand on desktop/tablet
      const shouldOpenLegend = window.innerWidth >= 768
      setLegendOpen(shouldOpenLegend)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (!mapId) {
      setError('No map ID provided in URL.')
      setLoading(false)
      return
    }

    const fetchPublishedMap = async () => {
      try {
        const response = await fetch(config.getApiUrl(`/regions/${mapId}/published`))
        if (!response.ok) {
          const errData = await response.json().catch(() => ({ error: 'Unknown error' }))
          throw new Error(errData.error || `Failed to fetch published map: ${response.status}`)
        }
        const data = await response.json()
        // console.log('[Viewer] Map data received:', data.map)
        setSvgUrl(data.map.svgUrl)
        setBackgroundUrl(data.map.backgroundUrl)
        setBackgroundSvgUrl(data.map.backgroundSvgUrl)
        setCompassUrl(data.map.compassUrl)
        setRegions(data.regionsSnapshot)
        setStatusColors(data.map.statusColors)
        setStageTemplate(data.map.stageTemplate || 'default')
        setButtonColors(data.map.buttonColors || null)
        setMapName(data.map.name || null)
        // Check if tenant name is in the map data
        if (data.map?.tenant?.name) {
          setTenantName(data.map.tenant.name)
        } else if (data.map?.tenantName) {
          setTenantName(data.map.tenantName)
        } else if (data.tenantName || data.tenant?.name) {
          setTenantName(data.tenantName || data.tenant?.name)
        }

        // Check if tenant contact info is in the map data
        if (data.map?.tenant?.contactEmail) {
          setTenantContactEmail(data.map.tenant.contactEmail)
        }
        if (data.map?.tenant?.website) {
          setTenantWebsite(data.map.tenant.website)
        }
        // setAnimateIntro(true) // animation disabled per request
      } catch (err) {
        console.error('Failed to fetch published map:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    const fetchStageLockInfo = async () => {
      try {
        const token = localStorage.getItem('token')
        const headers = {}
        if (token) {
          headers.Authorization = `Bearer ${token}`
        }
        const response = await fetch(config.getApiUrl(`/maps/${mapId}/stages`), {
          headers
        })
        if (response.ok) {
          const data = await response.json()
          const lockInfo = {}
          if (data.stages && Array.isArray(data.stages)) {
            data.stages.forEach(stage => {
              const stageName = typeof stage === 'string' ? stage : (stage && stage.name ? stage.name : 'Unknown')
              const isLocked = typeof stage === 'object' && stage ? (stage.isLocked || false) : false
              // Handle displayLabel: if it's explicitly null/undefined, use null; if it's an empty string, also use null; otherwise use the value
              const displayLabel = typeof stage === 'object' && stage
                ? (stage.displayLabel !== undefined && stage.displayLabel !== null && String(stage.displayLabel).trim() !== ''
                  ? String(stage.displayLabel).trim()
                  : null)
                : null
              if (stageName && stageName !== 'Unknown') {
                lockInfo[stageName] = { isLocked, displayLabel }
              }
            })
          }
          setStageLockInfo(lockInfo)
        } else {
          console.warn('Failed to fetch stage lock info:', response.status, response.statusText)
        }
      } catch (err) {
        console.error('Failed to fetch stage lock info:', err)
        // Don't set error for stage lock info, it's optional
      }
    }

    const fetchTenantLogo = async () => {
      try {
        const response = await fetch(config.getApiUrl(`/auth/tenant/logo/${mapId}`))
        if (response.ok) {
          const data = await response.json()
          setTenantLogo(data.logoUrl)
        }
      } catch (err) {
        console.error('Failed to fetch tenant logo:', err)
        // Don't set error for logo, it's optional
      }
    }

    const fetchTenantName = async () => {
      try {
        // Try to get tenant name from the map's tenant endpoint
        // First try the tenant logo endpoint pattern but for name
        const response = await fetch(config.getApiUrl(`/auth/tenant/name/${mapId}`))
        if (response.ok) {
          const data = await response.json()
          if (data.name || data.tenantName) {
            setTenantName(data.name || data.tenantName)
            return
          }
        }
      } catch (err) {
        // Continue to try alternative endpoint
      }

      // Try alternative endpoint - get tenant info from map
      try {
        const response = await fetch(config.getApiUrl(`/maps/${mapId}/tenant`))
        if (response.ok) {
          const data = await response.json()
          if (data.name || data.tenantName) {
            setTenantName(data.name || data.tenantName)
            return
          }
        }
      } catch (err2) {
        // Last resort: try to get from published map endpoint
        try {
          const response = await fetch(config.getApiUrl(`/maps/${mapId}/published`))
          if (response.ok) {
            const data = await response.json()
            if (data.tenantName || data.tenant?.name) {
              setTenantName(data.tenantName || data.tenant?.name)
              return
            }
          }
        } catch (err3) {
          console.warn('Could not fetch tenant name from any endpoint:', err3)
          // Don't set error for tenant name, it's optional
        }
      }
    }

    fetchPublishedMap()
    fetchTenantLogo()
    fetchTenantName()
    fetchStageLockInfo()
  }, [mapId])

  // Track previous activeStage to detect changes
  const prevActiveStageRef = useRef(null)
  const prevSelectedGroupRef = useRef(null)

  // Clear hover states when stage changes in admin mode
  useEffect(() => {
    if (isAdminMode && activeStage !== prevActiveStageRef.current) {
      // Clear all hover states immediately when a stage changes
      setHoveredRegion(null)
      setHoverPosition(null)
      if (setHoveredGroup) setHoveredGroup(null)
      setHoveredStage(null)
      prevActiveStageRef.current = activeStage
    }
  }, [activeStage, isAdminMode, setHoveredGroup])

  // Clear hover states when a group is selected in admin mode
  useEffect(() => {
    if (isAdminMode && selectedGroup !== prevSelectedGroupRef.current) {
      // Clear hover states when a group is selected (to ensure no colors show by default)
      setHoveredRegion(null)
      setHoverPosition(null)
      if (setHoveredGroup) setHoveredGroup(null)
      prevSelectedGroupRef.current = selectedGroup
    }
  }, [selectedGroup, isAdminMode, setHoveredGroup])

  const handleRegionHover = (region, point) => {
    // Disable hover effects on touch devices to prevent interference with zoom/pan
    // if (isTouch) {
    //   return
    // }

    // When no stage is active, hover should be stage-based, not lot-wise
    if (!activeStage) {
      if (region) {
        const stageKey = (region.name && String(region.name).trim()) || 'Ungrouped'
        setHoveredStage(stageKey)
      } else {
        setHoveredStage(null)
      }
      // Clear lot-level hover and tooltip when stage-based hover is active
      setHoveredRegion(null)
      setHoverPosition(null)
      // Clear hovered group when no stage is active
      if (setHoveredGroup) setHoveredGroup(null)
      return
    }

    // Inside an active stage, keep lot-wise hover for precision
    setHoveredRegion(region)
    setHoverPosition(point || null)

    // In admin mode, when hovering over a lot, also set hoveredGroup to highlight the group in sidebar
    // BUT: Don't set hoveredGroup if a group is already selected (to prevent color changes on hover)
    if (isAdminMode && setHoveredGroup && region && !selectedGroup) {
      const regionStageName = (region.name && String(region.name).trim()) || 'Ungrouped'
      if (region.customProps?.group) {
        // Lot has a group, set hoveredGroup to highlight it in sidebar
        setHoveredGroup({ stageName: regionStageName, groupName: region.customProps.group })
      } else {
        // Lot has no group, set hoveredGroup to "Ungrouped" to highlight it in sidebar
        setHoveredGroup({ stageName: regionStageName, groupName: 'Ungrouped' })
      }
    } else if (isAdminMode && setHoveredGroup && !region) {
      // Clear hovered group when not hovering over any region (only if no group is selected)
      if (!selectedGroup) {
        setHoveredGroup(null)
      }
    }
  }

  const handleStageHover = (stageKey) => {
    // Disable hover effects on touch devices to prevent interference with zoom/pan
    // if (isTouch) {
    //   return
    // }
    setHoveredStage(stageKey)
  }

  const handleRegionSelect = (region, point) => {
    if (!region) return

    // Ignore clicks that occur right after panning to prevent mistouches
    if (hasPannedRef.current) {
      return
    }

    const stageKey = (region.name && String(region.name).trim()) || 'Ungrouped'

    // If no stage is active yet, first activate the clicked region's stage.
    // Do not select the region on this first click to avoid opening details immediately.
    if (!activeStage) {
      setActiveStage(stageKey)
      setSelectedRegion(null)
      // In mobile view, open sidebar when stage is activated
      if (isNarrow) {
        setSidebarVisible(true)
      }
      return
    }

    // When a stage is already active, select the region (keeps highlight/side details).
    setSelectedRegion(region)

    // In mobile view, automatically open sidebar to show lot details
    if (isNarrow && !sidebarVisible) {
      setSidebarVisible(true)
    }

    // Record a lot click when a stage is active
    if (mapId && region?.id) {
      try {
        fetch(`${config.getApiUrl('/regions')}/${mapId}/${region.id}/click`, { method: 'POST' })
      } catch (e) {
        // ignore
      }
    }
  }

  // Clear transient hover when a stage becomes active or is cleared
  useEffect(() => {
    setHoveredStage(null)
  }, [activeStage])

  // Track panning to prevent click events after pan
  useEffect(() => {
    let isPointerDown = false
    let hasMoved = false

    const handlePointerDown = () => {
      isPointerDown = true
      hasMoved = false
      hasPannedRef.current = false
    }

    const handlePointerMove = () => {
      if (isPointerDown) {
        // If pointer moves while down, it's a pan/drag
        hasMoved = true
        hasPannedRef.current = true
        if (panTimeoutRef.current) {
          clearTimeout(panTimeoutRef.current)
        }
      }
    }

    const handlePointerUp = () => {
      if (hasMoved) {
        // If pointer moved while down, it was a pan - keep flag set briefly
        if (panTimeoutRef.current) {
          clearTimeout(panTimeoutRef.current)
        }
        // Clear pan flag after delay to prevent click from firing
        panTimeoutRef.current = setTimeout(() => {
          hasPannedRef.current = false
        }, 150)
      } else {
        // No movement, it was just a click - clear immediately
        hasPannedRef.current = false
      }
      isPointerDown = false
      hasMoved = false
    }

    // Add event listeners to window to catch all pointer events
    window.addEventListener('pointerdown', handlePointerDown)
    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown)
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      if (panTimeoutRef.current) {
        clearTimeout(panTimeoutRef.current)
      }
    }
  }, [])

  // Derive stages from regions (using name as Stage) - include locked stages but mark them as locked
  const stages = useMemo(() => {
    const bucket = {}
    const lockedStages = {}
    const displayLabels = {}

    regions.forEach(r => {
      // Check if region has meaningful data
      const hasDetails = Boolean(r.name || r.lotNumber || r.lotSize || r.price || r.notes)
      if (!hasDetails) return // Skip regions with no meaningful values

      const key = (r?.name && String(r.name).trim()) || 'Ungrouped'
      if (!bucket[key]) bucket[key] = 0
      bucket[key]++
    })

    // Add locked stages that don't have regions yet
    if (stageLockInfo) {
      Object.keys(stageLockInfo).forEach(stageName => {
        const stageInfo = stageLockInfo[stageName]
        const isLocked = typeof stageInfo === 'object' ? stageInfo.isLocked : stageInfo
        const displayLabel = typeof stageInfo === 'object' ? stageInfo.displayLabel : null


        if (isLocked && !bucket[stageName]) {
          bucket[stageName] = 0
          lockedStages[stageName] = true
          if (displayLabel) {
            displayLabels[stageName] = displayLabel
          }
        } else if (isLocked) {
          lockedStages[stageName] = true
          if (displayLabel) {
            displayLabels[stageName] = displayLabel
          }
        }
      })
    }

    const keys = Object.keys(bucket).sort((a, b) => {
      const na = Number(a), nb = Number(b)
      const aNum = !isNaN(na), bNum = !isNaN(nb)
      if (aNum && bNum) return na - nb
      if (aNum) return -1
      if (bNum) return 1
      return a.localeCompare(b)
    })
    return { keys, bucket, lockedStages, displayLabels }
  }, [regions, stageLockInfo])

  // Initial zoom target: null to show full plan (all lots) initially
  const initialZoomTarget = null

  const handleBackToTabs = () => {
    setViewMode('tabs')
    setSelectedRegion(null)
    setShowEnquiryForm(false)
    setEnquirySubmitted(false)
  }

  const handleEnquiryFormChange = (e) => {
    const { name } = e.target
    let { value } = e.target
    if (name === 'phone') {
      value = value.replace(/[^0-9]/g, '').slice(0, 15)
    }
    setEnquiryForm(prev => ({
      ...prev,
      [name]: value
    }))
    setEnquiryErrors(prev => {
      const msg = validateEnquiryField(name, value)
      const next = { ...prev }
      if (msg) next[name] = msg; else delete next[name]
      return next
    })
  }

  const validateEnquiryField = (field, value) => {
    const trimmed = typeof value === 'string' ? value.trim() : value
    switch (field) {
      case 'firstName': {
        if (!trimmed) return 'First name is required'
        if (/\d/.test(trimmed)) return 'First name cannot contain numbers'
        if (trimmed.length > 50) return 'First name cannot exceed 50 characters'
        return ''
      }
      case 'lastName': {
        if (trimmed && /\d/.test(trimmed)) return 'Last name cannot contain numbers'
        if (trimmed && trimmed.length > 50) return 'Last name cannot exceed 50 characters'
        return ''
      }
      case 'email': {
        if (!trimmed) return 'Email is required'
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(trimmed)) return 'Enter a valid email address'
        return ''
      }
      case 'phone': {
        if (!trimmed) return 'Phone number is required'
        if (!/^\d+$/.test(trimmed)) return 'Phone number must contain only digits'
        if (trimmed.length > 15) return 'Phone number cannot exceed 15 digits'
        return ''
      }
      case 'message': {
        if (!trimmed) return 'Message is required'
        if (/^\d/.test(trimmed)) return 'Message must not start with a number'
        if (trimmed.length > 1000) return 'Message cannot exceed 1000 characters'
        return ''
      }
      default:
        return ''
    }
  }

  const validateEnquiryForm = () => {
    const errors = {}
    const fields = ['firstName', 'lastName', 'email', 'phone', 'message']
    fields.forEach((f) => {
      const msg = validateEnquiryField(f, enquiryForm[f])
      if (msg) errors[f] = msg
    })
    setEnquiryErrors(errors)
    return Object.keys(errors).length === 0
  }

  const isEnquiryValid = useMemo(() => {
    const fields = ['firstName', 'lastName', 'email', 'phone', 'message']
    for (const f of fields) {
      if (validateEnquiryField(f, enquiryForm[f])) return false
    }
    return true
  }, [enquiryForm])

  const handleEnquirySubmit = async (e) => {
    e.preventDefault()
    // Frontend validation
    const ok = validateEnquiryForm()
    if (!ok) return

    setEnquirySubmitting(true)
    setEnquiryErrors({})

    try {
      const enquiryData = {
        name: `${enquiryForm.firstName.trim()}${enquiryForm.lastName.trim() ? ' ' + enquiryForm.lastName.trim() : ''}`,
        email: enquiryForm.email,
        phone: enquiryForm.phone,
        message: enquiryForm.message,
        // Always send lotId - use actual lot ID if selected, otherwise use placeholder for general enquiries
        lotId: selectedRegion ? selectedRegion.id : 'GENERAL_ENQUIRY',
        ...(selectedRegion ? {
          lotNumber: selectedRegion.lotNumber,
          lotName: selectedRegion.name,
          lotSize: selectedRegion.lotSize,
          lotPrice: selectedRegion.price,
          lotFrontage: selectedRegion.customProps?.frontage,
          lotAspect: selectedRegion.customProps?.aspect,
        } : {
          // For general enquiries (no lot selected), send null for optional lot fields
          lotNumber: null,
          lotName: null,
          lotSize: null,
          lotPrice: null,
          lotFrontage: null,
          lotAspect: null,
        }),
        mapId: mapId,
        submittedAt: new Date().toISOString()
      }

      const response = await fetch(`${config.API_BASE}/enquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(enquiryData)
      })

      if (response.ok) {
        setEnquirySubmitted(true)
        setEnquirySubmitting(false)
        // Reset form after 3 seconds
        setTimeout(() => {
          setEnquiryForm({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            message: ''
          })
          setEnquiryErrors({})
          setEnquirySubmitted(false)
          setShowEnquiryForm(false)
          setShowEnquiryFormOverlay(false)
        }, 3000)
      } else {
        setEnquirySubmitting(false)
        const errData = await response.json().catch(() => ({}))
        alert.error(errData.error || 'Failed to send enquiry. Please try again.')
      }
    } catch (error) {
      console.error('Error sending enquiry:', error)
      setEnquirySubmitting(false)
      alert.error('Error sending enquiry. Please try again.')
    }
  }

  const handleSidebarClose = () => {
    setSidebarAnimating(true)
    // Delay the actual state change to allow animation to complete
    setTimeout(() => {
      setSidebarVisible(false)
      setSidebarAnimating(false)
    }, 400) // Match the animation duration (0.4s)
  }

  const handleSetActiveStage = useCallback((k) => {
    setActiveStage(k)
    setSelectedRegion(null)
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedRegion(null)
  }, [])

  // Handle shape data from Svg component
  const handleShapeData = (region, shape, viewBox) => {
    // console.log('Viewer: Received shape data:', {
    //   regionId: region?.id,
    //   lotNumber: region?.lotNumber,
    //   hasShape: !!shape,
    //   hasViewBox: !!viewBox
    // })
    setHoveredShape(shape)
    setSvgViewBox(viewBox)
  }

  // Receive shapes map from Svg for thumbnail generation
  const handleShapesMap = (shapeMap, viewBox) => {
    setRegionShapeMap(shapeMap)
    if (viewBox) setSvgViewBox(viewBox)
  }

  // Helper to provide lot thumbnails to the sidebar
  const getLotThumbnail = useCallback(async (region, maxSize = 200) => {
    try {
      if (!region || !backgroundImageElement || !svgViewBox || !regionShapeMap) return null
      const shape = regionShapeMap.get(region.id)
      if (!shape) return null
      const { getLotImage } = await import('../utils/lotImageExtractor')
      const cacheKey = `${mapId || 'map'}:${region.id}:${maxSize}`
      const dataUrl = await getLotImage(cacheKey, backgroundImageElement, svgViewBox, shape, maxSize)
      return dataUrl
    } catch (_) {
      return null
    }
  }, [backgroundImageElement, svgViewBox, regionShapeMap, mapId])

  // Set document title based on map name
  useEffect(() => {
    if (mapName) {
      document.title = mapName
    } else {
      document.title = 'React App'
    }
    return () => {
      document.title = 'React App'
    }
  }, [mapName])

  // Load background image element for cropping
  useEffect(() => {
    if (!backgroundUrl) {
      setBackgroundImageElement(null)
      return
    }

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      // console.log('Background image loaded for lot cropping:', backgroundUrl)
      // Clear cache when background image changes
      clearLotImageCache()
      setBackgroundImageElement(img)
    }
    img.onerror = () => {
      console.warn('Failed to load background image for lot cropping')
      setBackgroundImageElement(null)
    }
    img.src = config.getApiUrl(backgroundUrl)
  }, [backgroundUrl])

  const stageSoldMap = useMemo(() => {
    if (!regions || regions.length === 0) return {}
    const byStage = {}
    regions.forEach(region => {
      const stageKey = (region?.name && String(region.name).trim()) || 'Ungrouped'
      if (!byStage[stageKey]) byStage[stageKey] = []
      byStage[stageKey].push(region)
    })
    const result = {}
    Object.entries(byStage).forEach(([stageKey, lots]) => {
      const checkable = lots.filter(lot => {
        const hasDetails = Boolean(lot.name || lot.lotNumber || lot.lotSize || lot.price || lot.notes)
        return hasDetails && lot.status !== STATUS.LOCKED
      })
      result[stageKey] = checkable.length > 0 && checkable.every(lot => lot.status === STATUS.SOLD)
    })
    return result
  }, [regions])

  useEffect(() => {
    if (!backgroundSvgUrl) {
      setOverlayImageElement(null)
      return
    }
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => setOverlayImageElement(img)
    img.onerror = () => {
      console.warn('Failed to load overlay image for exports')
      setOverlayImageElement(null)
    }
    img.src = config.getApiUrl(backgroundSvgUrl)
  }, [backgroundSvgUrl])

  if (loading) return <div className="viewer-message">Loading Master Plan...</div>
  if (error) return <div className="viewer-message error">{error}</div>
  if (!svgUrl) return <div className="viewer-message">No published version found for this map.</div>

  return (
    <div className="viewer-container" style={{ position: 'relative', overflow: 'visible !important', flexDirection: isNarrow ? 'column' : 'row', height: '100vh', overflow: 'hidden' }}>
      <div style={{ width: isNarrow ? '100%' : '65%', position: 'relative', zIndex: 1, minHeight: isNarrow ? (sidebarVisible ? '55vh' : '100vh') : 'auto' }}>
        {/* Logo overlay in top left corner of canvas */}
        {tenantLogo && (
          <div className="viewer-logo" style={{
            position: 'absolute',
            top: '12px',
            left: '12px',
            zIndex: 9999,
            width: 'clamp(130px, 22vw, 240px)',
            height: 'clamp(65px, 12vh, 120px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none'
          }}>
            <img
              src={config.getApiUrl(tenantLogo)}
              alt="Company Logo"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                display: 'block',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
              }}
              onError={(e) => {
                e.target.style.display = 'none'
              }}
            />
          </div>
        )}
        {/* Initial loader overlay to avoid black screen while textures/SVG paint */}
        {!assetsReady && (
          <div className="viewer-initial-loader" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0b0b0b', color: '#fff', zIndex: 10 }}>
            <div className="spinner" aria-label="Loading map assets" />
          </div>
        )}
        {/* Toggle button for mobile/tablet */}
        {isNarrow && (
          <button
            onClick={() => setSidebarVisible(!sidebarVisible)}
            className="mobile-sidebar-toggle"
          >
            {sidebarVisible ? '▼' : '▲'}
          </button>
        )}
        <Canvas
          frameloop={'demand'}
          orthographic
          camera={{ position: [0, 0, 50], zoom: cameraZoom, up: [0, 0, 1], far: 10000 }}
          onPointerMissed={() => {
            setHoveredRegion(null)
            setHoverPosition(null)
          }}
        >
          <Suspense fallback={null}>
            <Svg
              url={config.getApiUrl(svgUrl)}
              regions={regions}
              onHover={handleRegionHover}
              onSelect={handleRegionSelect}
              selectedRegion={selectedRegion}
              hoveredRegion={hoveredRegion}
              // animateIntro={animateIntro}
              // onIntroDone={() => setAnimateIntro(false)}
              activeStage={activeStage}
              hoveredStage={hoveredStage}
              mapControlsRef={controlsRef}
              backgroundUrl={backgroundUrl ? config.getApiUrl(backgroundUrl) : null}
              backgroundSvgUrl={backgroundSvgUrl ? config.getApiUrl(backgroundSvgUrl) : null}
              onShapeData={handleShapeData}
              onShapesMap={handleShapesMap}
              onReady={() => setAssetsReady(true)}
              stageLockInfo={stageLockInfo}
              isTouch={isTouch}
              initialZoomToStage={initialZoomTarget}
              isAdminMode={isAdminMode}
              selectedGroup={selectedGroup}
              hoveredGroup={hoveredGroup}
              activeTab={activeTab}
              statusColors={statusColors}
              soldStages={stageSoldMap}
            />
          </Suspense>
          {/* {!isTouch && ( */}
          <HoverTooltip
            key={hoveredRegion?.id || 'no-region'}
            region={hoveredRegion}
            position={hoverPosition}
            activeStage={activeStage}
            stages={stages}
            backgroundImage={backgroundImageElement}
            svgBounds={svgViewBox}
            shape={hoveredShape}
            logoUrl={tenantLogo ? config.getApiUrl(tenantLogo) : null}
          />
          {/* )} */}
          <MapControls 
            ref={controlsRef} 
            enableRotate={false} 
            enableZoom={true} 
            enablePan={true} 
            zoomSpeed={0.9}
            minZoom={0.4}
            maxZoom={8}
            panSpeed={1.0}
            screenSpacePanning={false}
          />
        </Canvas>
        {/* Bottom-left legend: collapsible + hidden when a stage is active */}
        {/* {!activeStage && (
          <div className={`map-legend ${legendOpen ? 'open' : 'closed'}`}>
            <button className="map-legend-toggle" onClick={() => setLegendOpen(!legendOpen)} aria-expanded={legendOpen} aria-label={legendOpen ? 'Hide color guide' : 'Show color guide'}>
              {legendOpen ? 'Color Guide' : 'Color Guide'}
            </button>
            {legendOpen && (
              <div className="map-legend-content">
                <div className="map-legend-row">
                  <span className="legend-swatch" style={{ backgroundColor: statusToColor(STATUS.AVAILABLE, statusColors) }} />
                  <span className="legend-label">Available</span>
                </div>
                <div className="map-legend-row">
                  <span className="legend-swatch" style={{ backgroundColor: statusToColor(STATUS.SOLD, statusColors) }} />
                  <span className="legend-label">Sold</span>
                </div>
                <div className="map-legend-row">
                  <span className="legend-swatch" style={{ backgroundColor: statusToColor(STATUS.LOCKED, statusColors) }} />
                  <span className="legend-label">Locked</span>
                </div>
              </div>
            )}
          </div>
        )} */}
        {/* Compass overlay using image */}
        {compassUrl && (
          <div className="map-compass">
            <img
              src={config.getApiUrl(compassUrl)}
              alt="North Compass"
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              onError={(e) => { e.currentTarget.style.display = 'none' }}
            />
          </div>
        )}
      </div>

      <div
        className={`${isNarrow ? (sidebarVisible ? 'mobile-sidebar-visible' : 'mobile-sidebar-hidden') : ''}`}
        style={{
          width: isNarrow ? '100%' : '35%',
          minWidth: isNarrow ? '100%' : '23%',
          maxWidth: isNarrow ? '100%' : '35%',
          height: isNarrow ? '45vh' : '100%',
          overflowY: 'auto',
          background: '#fff',
          zIndex: 2,
          boxShadow: isNarrow ? '0 -1px 0 rgba(0,0,0,0.06), 0 -8px 24px rgba(0,0,0,0.06)' : '0 0 0 1px rgba(0,0,0,0.06), -8px 0 24px rgba(0,0,0,0.06)',
          display: isNarrow && !sidebarVisible && !sidebarAnimating ? 'none' : 'block'
        }}
      >
        <Suspense fallback={<div className="viewer-message">Loading panel...</div>}>
          <StageSidebar
            regions={regions}
            stages={stages}
            activeStage={activeStage}
            setActiveStage={handleSetActiveStage}
            hoveredRegion={hoveredRegion}
            hoveredStage={hoveredStage}
            onRegionHover={handleRegionHover}
            onRegionSelect={handleRegionSelect}
            onStageHover={handleStageHover}
            selectedRegion={selectedRegion}
            clearSelection={clearSelection}
            showEnquiryForm={showEnquiryForm}
            setShowEnquiryForm={setShowEnquiryForm}
            showEnquiryFormOverlay={showEnquiryFormOverlay}
            setShowEnquiryFormOverlay={setShowEnquiryFormOverlay}
            enquiryForm={enquiryForm}
            enquiryErrors={enquiryErrors}
            isEnquiryValid={isEnquiryValid}
            handleEnquiryFormChange={handleEnquiryFormChange}
            handleEnquirySubmit={handleEnquirySubmit}
            enquirySubmitted={enquirySubmitted}
            enquirySubmitting={enquirySubmitting}
            downloadUrl={backgroundUrl ? config.getApiUrl(backgroundUrl) : null}
            onClose={handleSidebarClose}
            viewMode={lotListViewMode}
            setViewMode={setLotListViewMode}
            getLotThumbnail={getLotThumbnail}
            regionShapeMap={regionShapeMap}
            svgViewBox={svgViewBox}
            backgroundImageElement={backgroundImageElement}
            overlayImageElement={overlayImageElement}
            svgUrl={svgUrl ? config.getApiUrl(svgUrl) : null}
            mapName={mapName}
            tenantLogo={tenantLogo ? config.getApiUrl(tenantLogo) : null}
            tenantName={tenantName}
            tenantContactEmail={tenantContactEmail}
            tenantWebsite={tenantWebsite}
            statusColors={statusColors}
            stageTemplate={stageTemplate}
            buttonColors={buttonColors}
            isAdminMode={isAdminMode}
            selectedGroup={selectedGroup}
            setSelectedGroup={setSelectedGroup}
            hoveredGroup={hoveredGroup}
            setHoveredGroup={setHoveredGroup}
            onActiveTabChange={setActiveTab}
            soldStages={stageSoldMap}
          />
        </Suspense>
      </div>

      {/* Removed stage chips; selection now in sidebar */}
    </div>
  )
}